/**
 * «Убрать культуру из плана» — обратный ход к заведению культуры.
 *
 * Без него ошибка ввода становится тупиком: строку плана иначе не снять ничем,
 * кроме обхода карточек полей по одной. Подтверждение спрашиваем словами
 * агронома («освободятся поля»), а посевы варианта архивируются, а не
 * стираются: вариант плана — черновик, и «нажал не туда» не должно означать
 * потерю данных.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCan } from '@/lib/auth/usePermissions';
import { useRemoveCropFromVariant } from '@/lib/planning/planCrops';

export function RemoveCropButton({
  scenarioId,
  cropId,
  cropName,
  predecessorCropId,
  predecessorName,
  fieldsCount,
}: {
  scenarioId: string;
  cropId: string;
  cropName: string;
  /** Задан — убираем только эту строку; иначе культуру целиком. */
  predecessorCropId: string | null;
  predecessorName: string | null;
  fieldsCount: number;
}) {
  const canRemove = useCan('sowings', 'd');
  const remove = useRemoveCropFromVariant();
  const [open, setOpen] = useState(false);

  if (!canRemove) return null;

  const rowTitle = predecessorName
    ? `${cropName} по ${predecessorName.toLowerCase()}`
    : cropName;

  return (
    <>
      <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)}>
        Убрать культуру из плана
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Убрать «{rowTitle}» из плана?</DialogTitle>
            <DialogDescription>
              Освободится полей: {fieldsCount}. Их можно будет занять другой культурой.
              Назначенная этой строке техкарта останется в сезоне — из плана уходит только
              сама посевная площадь. Факт прошлых сезонов не затрагивается.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Оставить
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(
                  { scenarioId, cropId, cropName, predecessorCropId },
                  { onSuccess: () => setOpen(false) },
                )
              }
            >
              {remove.isPending ? 'Убираем…' : 'Убрать из плана'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
